import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, CheckCircle2, DatabaseBackup, HardDrive, UploadCloud } from 'lucide-react';
import { CountUp } from '@/components/CountUp';
import { fmtBytes } from '@/lib/format';
import type { OverviewMedia } from '@/types';
import './file-pipeline.css';

type Stage = {
  key: string;
  label: string;
  value: number;
  hint: string;
  tone: 'info' | 'success' | 'neutral';
  Icon: typeof UploadCloud;
  href?: string;
  meter?: number;
};

/** 總覽頁的檔案流程：待完成傳輸 → 已保存 → 已備份，失敗另外一列。
 *  數字放在文字欄裡，不再塞進 44px 的方塊；七位數也不會超出框。
 *  排法依卡片寬度切換（見 file-pipeline.css），手機直排、桌面橫排。 */
export function FilePipeline({ media, canOpenBackup }: { media: OverviewMedia; canOpenBackup: boolean }) {
  // 只取整數百分比；有備份但不到 1% 時另外說明，避免顯示成「0%」被誤會成沒有備份
  const ratio = media.stored > 0 ? Math.min(100, Math.floor((media.backed_up / media.stored) * 100)) : undefined;
  const backupHint = ratio === undefined ? '尚無可備份的檔案'
    : media.backed_up === 0 ? '還沒有備份任何檔案'
      : ratio === 0 ? '不到已保存檔案的 1%'
        : `佔已保存檔案的 ${ratio}%`;

  const stages: Stage[] = [
    {
      key: 'in_flight', label: '待完成傳輸', value: media.in_flight, tone: 'info', Icon: UploadCloud,
      hint: media.in_flight > 0 ? '待上傳、上傳中、驗證中' : '目前沒有進行中的傳輸',
      href: '/media?status=in_flight',
    },
    {
      key: 'stored', label: '已保存', value: media.stored, tone: 'success', Icon: HardDrive,
      hint: media.stored > 0 ? `共 ${fmtBytes(media.stored_bytes)}` : '尚無已保存檔案',
      href: '/media?status=stored',
    },
    {
      key: 'backed_up', label: '已備份', value: media.backed_up, tone: 'neutral', Icon: DatabaseBackup,
      hint: backupHint, href: canOpenBackup ? '/backup' : undefined, meter: ratio,
    },
  ];

  return (
    <div className="file-pipeline">
      <ol role="list" aria-label="檔案流程" className="file-pipeline-stages">
        {stages.map(({ key, label, value, hint, tone, Icon, href, meter }) => {
          const body = (
            <>
              <span className="file-pipeline-node" aria-hidden="true"><Icon className="h-[18px] w-[18px]" /></span>
              <span className="file-pipeline-label">{label}</span>
              <CountUp value={value} className="file-pipeline-value tabular" />
              <span className="file-pipeline-hint">{hint}</span>
              {meter !== undefined && (
                <span className="file-pipeline-meter" aria-hidden="true">
                  <span style={{ '--fp-meter': `${meter}%` } as CSSProperties} />
                </span>
              )}
            </>
          );
          return (
            <li key={key} className="file-pipeline-stage" data-tone={tone}>
              {href
                ? <Link to={href} className="file-pipeline-body">{body}</Link>
                : <div className="file-pipeline-body">{body}</div>}
            </li>
          );
        })}
      </ol>

      {media.failed > 0 ? (
        <Link to="/media?status=failed" className="file-pipeline-failed" data-state="alert">
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            <span className="tabular">{media.failed.toLocaleString('zh-TW')}</span> 個檔案傳輸失敗
          </span>
          <span className="file-pipeline-failed-action">
            查看失敗檔案<ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" />
          </span>
        </Link>
      ) : (
        <p className="file-pipeline-failed" data-state="clear">
          <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />目前沒有傳輸失敗
        </p>
      )}
    </div>
  );
}
